import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Skeleton } from 'designSystem/components/Skeleton'
import { Text } from 'designSystem/components/Text'
import { fmtWhen } from 'features/fimReview/relativeTime'
import type {
  ConversationSummary,
  useConversations,
} from 'features/timeSeries/agentTab/useConversations'
import { Check, Pencil, Trash2, X } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

/**
 * Past conversations at this site.
 *
 * Shared rather than private: a build is a plant record, so the engineer on the
 * next shift opens the one from this morning instead of starting over. The
 * transcript lives in Rolodex, which is why this list can exist at all.
 */

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 18rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
`

const List = styled.div`
  overflow-y: auto;
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  padding: 0.25rem;
  gap: 0.125rem;
`

const Row = styled.div<{ $current: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.375rem;
  border-radius: 0.25rem;
  background: ${({ $current, theme }) =>
    $current ? theme.forecast.activeGhostButton : 'transparent'};
  &:hover {
    background: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
`

const Open = styled.button`
  all: unset;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 1px;
  }
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 0;
`

const RenameRow = styled.form`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
`

const RenameInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  color: inherit;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  padding: 0.1875rem 0.375rem;
  font: inherit;
  ${mobileInputTextStyles}
`

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.5rem;
`

const SkeletonRow = styled(Skeleton)`
  height: 1.5rem;
  width: 100%;
`

const subtitle = (conversation: ConversationSummary): string =>
  [
    fmtWhen(conversation.updated_at),
    conversation.last_author,
    conversation.turn_count
      ? plural(conversation.turn_count, {
          one: '# message',
          other: '# messages',
        })
      : '',
  ]
    .filter(Boolean)
    .join(' · ')

export const ConversationDrawer: React.FC<{
  conversations: ReturnType<typeof useConversations>
  currentId: string | null
  onOpen: (id: string) => void
}> = ({ conversations, currentId, onOpen }) => {
  const { conversations: list, loading, error, rename, remove } = conversations
  const { t } = useLingui()
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')

  if (loading && !list.length) {
    return (
      <Panel>
        <Placeholder aria-busy='true' aria-label={t`Loading conversations`}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </Placeholder>
      </Panel>
    )
  }

  if (error) {
    return (
      <Panel>
        <Placeholder>
          <Text variant='caption' color='destructive'>
            {error}
          </Text>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void conversations.refresh()}>
            {t`Try again`}
          </Button>
        </Placeholder>
      </Panel>
    )
  }

  if (!list.length) {
    return (
      <Panel>
        <Placeholder>
          <Text variant='caption' color='muted'>
            {t`No conversations at this site yet. The one you start will be here for everyone.`}
          </Text>
        </Placeholder>
      </Panel>
    )
  }

  return (
    <Panel>
      <List>
        {list.map((conversation) => {
          const title = conversation.title
          return (
            <Row key={conversation.id} $current={conversation.id === currentId}>
              {renaming === conversation.id ? (
                <RenameRow
                  onSubmit={(event) => {
                    event.preventDefault()
                    const title = draftTitle.trim()
                    if (title) void rename(conversation.id, title)
                    setRenaming(null)
                  }}>
                  <RenameInput
                    autoFocus
                    value={draftTitle}
                    aria-label={t`Conversation name`}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') setRenaming(null)
                    }}
                  />
                  <Button
                    type='submit'
                    variant='ghost'
                    size='icon-sm'
                    aria-label={t`Save name`}>
                    <Check />
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon-sm'
                    aria-label={t`Cancel rename`}
                    onClick={() => setRenaming(null)}>
                    <X />
                  </Button>
                </RenameRow>
              ) : (
                <>
                  <Open
                    type='button'
                    title={t`Open this conversation`}
                    onClick={() => onOpen(conversation.id)}>
                    <Text variant='body' truncate>
                      {conversation.title}
                    </Text>
                    <Text variant='caption' color='tertiary' truncate>
                      {subtitle(conversation)}
                    </Text>
                  </Open>
                  <Actions>
                    <Button
                      variant='ghostMuted'
                      size='icon-sm'
                      aria-label={t`Rename ${title}`}
                      onClick={() => {
                        setRenaming(conversation.id)
                        setDraftTitle(conversation.title)
                      }}>
                      <Pencil />
                    </Button>
                    <Button
                      variant='ghostMuted'
                      size='icon-sm'
                      aria-label={t`Delete ${title}`}
                      onClick={() => {
                        if (
                          window.confirm(
                            t`Delete “${title}”? Everyone at this site loses the transcript. Saved models are not affected.`
                          )
                        ) {
                          void remove(conversation.id)
                        }
                      }}>
                      <Trash2 />
                    </Button>
                  </Actions>
                </>
              )}
            </Row>
          )
        })}
      </List>
    </Panel>
  )
}
